import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, finalize, Observable, map, of, Subject, takeUntil, throwError, forkJoin } from 'rxjs';
import KeyEnum = Boo.Objects.Enums.KeyEnum;
import TaskTypeEnum = Boo.Objects.Enums.TaskTypeEnum;
import WorkflowHelper from '../../../../../../shared/WorkflowHelper';
import { WorkflowKeyValuePairService } from '../../../../../../services/workflow-keyvaluepair.service';
import { PageOptimizationAudit } from '../../../../../../shared/models/page-optimizations/page-optimizations';
import { SaveTypes } from 'app/models/enums/SaveTypes';
import { TaskKeyValuePairService } from '../../../../../../services/task-keyvaluepair.service';
import TaskHelper from 'app/specialist/TaskHelper';
import Task = Boo.Objects.Task;

@Component({
  selector: 'app-components-specialist-actioncomponents-page-optimization-shared-audit',
  templateUrl: './audit.component.html',
  styleUrls: ['./audit.component.scss']
})
export class PageOptimizationAuditComponent implements OnInit, OnDestroy {
  @Input() orderingTask: Task;
  @Input() workflowId: number;
  @Input() taskId: number;
  @Input() userId: number;
  @Input() customerId: number;
  @Input() websiteUrlId: number;
  @Input() createSnapshot = false;
  @Input() isReadOnly = false;
  @Output() auditChanged = new EventEmitter<void>();

  formGroup: FormGroup;
  conversionLanguageInPlaceControl: FormControl<boolean>;
  duplicateContentControl: FormControl<boolean>;
  headingsStructureControl: FormControl<boolean>;
  imageOptimizationControl: FormControl<boolean>;
  metaRobotsControl: FormControl<boolean>;
  redirectsControl: FormControl<boolean>;
  robotsTxtFileControl: FormControl<boolean>;
  urlStructureControl: FormControl<boolean>;

  isLoading = true;
  isRequired = false;
  workflowKeyValuePairs: Boo.Objects.WorkflowKeyValuePair[] = [];
  pageOptimizationAudit: PageOptimizationAudit;

  private destroy$ = new Subject<void>();

  constructor(
    private taskKeyValuePairService: TaskKeyValuePairService,
    private workflowKeyValuePairService: WorkflowKeyValuePairService) { }

  ngOnInit(): void {
    this.isRequired = this.orderingTask.TaskTypeEnum === TaskTypeEnum.PageOptimization;

    if (this.isRequired || this.isReadOnly) {
      this.workflowKeyValuePairService.getByWorkflowId(this.workflowId)
        .pipe(
          finalize(() => this.isLoading = false)
        )
        .subscribe({
          next: (workflowKeyValuePairs) => {
            this.workflowKeyValuePairs = workflowKeyValuePairs;
            this.pageOptimizationAudit = WorkflowHelper.getJSON<PageOptimizationAudit>(workflowKeyValuePairs, KeyEnum.PageOptimizationAudit) || this.createPageOptimizationAudit(this.customerId, this.websiteUrlId);

            this.setupForm();
          },
          error: (err) => toastr.error('Failed to load audit:', err)
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // This form does not have to be valid for SaveTypes.Save
  isValid(saveType: SaveTypes): boolean {
    if (this.isReadOnly || !this.isRequired) {
      return true;
    }

    this.validateAllControls(false);

    if (!this.formGroup.valid && saveType !== SaveTypes.Save) {
      this.validateAllControls(true);
      return false;
    }

    return true;
  }

  save(saveType: SaveTypes): Observable<void> {
    if (this.isReadOnly || !this.isValid(saveType) || !this.isRequired) {
      return of(void 0);
    }

    const pageOptimizationAudit: PageOptimizationAudit = {
      conversionLanguageInPlace: this.conversionLanguageInPlaceControl.value,
      customerId: this.customerId,
      duplicateContent: this.duplicateContentControl.value,
      headingsStructure: this.headingsStructureControl.value,
      imageOptimization: this.imageOptimizationControl.value,
      metaRobots: this.metaRobotsControl.value,
      redirects: this.redirectsControl.value,
      robotsTxtFile: this.robotsTxtFileControl.value,
      urlStructure: this.urlStructureControl.value,
      websiteUrlId: this.websiteUrlId
    };

    // Saved consistently to the workflow for deliverable and to the task for historical purposes
    WorkflowHelper.setJSON(this.workflowKeyValuePairs, this.workflowId, KeyEnum.PageOptimizationAudit, pageOptimizationAudit);
    const saveMethods = [this.workflowKeyValuePairService.save([WorkflowHelper.getWorkflowKeyValuePair(this.workflowKeyValuePairs, KeyEnum.PageOptimizationAudit)])];

    if ([SaveTypes.Complete, SaveTypes.Reject].includes(saveType) && this.createSnapshot) {
      const taskKeyValuePairs: Boo.Objects.TaskKeyValuePair[] = [];
      TaskHelper.setJSON(taskKeyValuePairs, this.taskId, this.userId, KeyEnum.PageOptimizationAudit, pageOptimizationAudit);
      saveMethods.push(this.taskKeyValuePairService.save([TaskHelper.getTaskKeyValuePair(taskKeyValuePairs, KeyEnum.PageOptimizationAudit)]));
    }

    return forkJoin(saveMethods)
      .pipe(
        map(() => void 0),
        catchError(err => {
          toastr.error('Failed to save Page Optimization audit:', err);
          return throwError(() => err);
        })
      );
  }

  private setupForm(): void {
    this.conversionLanguageInPlaceControl = new FormControl(this.pageOptimizationAudit.conversionLanguageInPlace, [ Validators.required ]);
    this.duplicateContentControl = new FormControl(this.pageOptimizationAudit.duplicateContent, [ Validators.required ]);
    this.headingsStructureControl = new FormControl(this.pageOptimizationAudit.headingsStructure, [ Validators.required ]);
    this.imageOptimizationControl = new FormControl(this.pageOptimizationAudit.imageOptimization, [ Validators.required ]);
    this.metaRobotsControl = new FormControl(this.pageOptimizationAudit.metaRobots, [ Validators.required ]);
    this.redirectsControl = new FormControl(this.pageOptimizationAudit.redirects, [ Validators.required ]);
    this.robotsTxtFileControl = new FormControl(this.pageOptimizationAudit.robotsTxtFile, [ Validators.required ]);
    this.urlStructureControl = new FormControl(this.pageOptimizationAudit.urlStructure, [ Validators.required ]);

    this.formGroup = new FormGroup({
      conversionLanguageInPlace: this.conversionLanguageInPlaceControl,
      duplicateContent: this.duplicateContentControl,
      headingsStructure: this.headingsStructureControl,
      imageOptimization: this.imageOptimizationControl,
      metaRobots: this.metaRobotsControl,
      redirects: this.redirectsControl,
      robotsTxtFile: this.robotsTxtFileControl,
      urlStructure: this.urlStructureControl
    });

    setTimeout(() => {
      this.formGroup.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.auditChanged.emit();
        });
    }, 10);
  }

  private createPageOptimizationAudit(customerId: number, websiteUrlId: number): PageOptimizationAudit {
    return {
      customerId: customerId,
      websiteUrlId: websiteUrlId
    };
  }

  private validateAllControls(emitEvent: boolean) : void {
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.controls[key].markAsDirty();
      this.formGroup.controls[key].updateValueAndValidity({ emitEvent: emitEvent });
    });
  }
}